import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import HintTooltip from '../common/HintTooltip';
import { instanciaService, MetaTemplate, MetaTemplateComponent, CreateTemplateData } from '../../services/instanciaService';
import { useToast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Plus, Trash2 } from 'lucide-react';

interface MetaTemplateFormDialogProps {
  instanciaId: string;
  open: boolean;
  onClose: () => void;
  template?: MetaTemplate | null;
  onSaved: () => void;
}

const LANGUAGES = [
  { value: 'pt_BR', label: 'Português (BR)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'es', label: 'Español' },
  { value: 'es_ES', label: 'Español (ES)' },
  { value: 'es_MX', label: 'Español (MX)' },
  { value: 'fr_FR', label: 'Français' },
  { value: 'de_DE', label: 'Deutsch' },
  { value: 'it_IT', label: 'Italiano' },
  { value: 'ja_JP', label: '日本語' },
  { value: 'ko_KR', label: '한국어' },
  { value: 'zh_CN', label: '中文 (简体)' },
  { value: 'ar_AR', label: 'العربية' },
  { value: 'hi_IN', label: 'हिन्दी' },
];

// Rótulos no i18n: templates.category.<valor> e templateForm.headerFormats.<valor>.
const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];

const HEADER_FORMATS = ['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];

// Os marcadores {{1}}, {{2}} das variáveis da Meta entram por interpolação para
// o i18next não confundi-los com as próprias variáveis dele.
const VAR_PLACEHOLDERS = { v1: '{{1}}', v2: '{{2}}' };

const extractVariables = (text: string): string[] => {
  const matches = text.match(/\{\{(\d+)\}\}/g);
  if (!matches) return [];
  return [...new Set(matches)].sort((a, b) => {
    const numA = parseInt(a.replace(/[{}]/g, ''));
    const numB = parseInt(b.replace(/[{}]/g, ''));
    return numA - numB;
  });
};

const MetaTemplateFormDialog: React.FC<MetaTemplateFormDialogProps> = ({
  instanciaId,
  open,
  onClose,
  template,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isEditing = !!template;

  const [name, setName] = useState('');
  const [language, setLanguage] = useState('pt_BR');
  const [category, setCategory] = useState('MARKETING');
  const [saving, setSaving] = useState(false);

  const [headerEnabled, setHeaderEnabled] = useState(false);
  const [headerFormat, setHeaderFormat] = useState('TEXT');
  const [headerText, setHeaderText] = useState('');
  const [headerExamples, setHeaderExamples] = useState<string[]>([]);

  const [bodyText, setBodyText] = useState('');
  const [bodyExamples, setBodyExamples] = useState<string[]>([]);

  const [footerEnabled, setFooterEnabled] = useState(false);
  const [footerText, setFooterText] = useState('');

  const [buttonsEnabled, setButtonsEnabled] = useState(false);
  const [buttons, setButtons] = useState<Array<{ type: string; text: string; url: string }>>([]);

  const bodyVariables = useMemo(() => extractVariables(bodyText), [bodyText]);
  const headerVariables = useMemo(() => (headerFormat === 'TEXT' ? extractVariables(headerText) : []), [headerText, headerFormat]);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setLanguage(template.language);
      setCategory(template.category);

      const header = template.components?.find(c => c.type === 'HEADER');
      if (header) {
        setHeaderEnabled(true);
        setHeaderFormat(header.format || 'TEXT');
        setHeaderText(header.text || '');
        if (header.example?.header_text?.[0]) {
          setHeaderExamples(header.example.header_text[0]);
        }
      }

      const body = template.components?.find(c => c.type === 'BODY');
      if (body) {
        setBodyText(body.text || '');
        if (body.example?.body_text?.[0]) {
          setBodyExamples(body.example.body_text[0]);
        }
      }

      const footer = template.components?.find(c => c.type === 'FOOTER');
      if (footer) {
        setFooterEnabled(true);
        setFooterText(footer.text || '');
      }

      const btns = template.components?.find(c => c.type === 'BUTTONS');
      if (btns?.buttons?.length) {
        setButtonsEnabled(true);
        setButtons(btns.buttons.map(b => ({
          type: b.type || 'QUICK_REPLY',
          text: b.text || '',
          url: b.url || '',
        })));
      }
    } else {
      setName('');
      setLanguage('pt_BR');
      setCategory('MARKETING');
      setHeaderEnabled(false);
      setHeaderFormat('TEXT');
      setHeaderText('');
      setHeaderExamples([]);
      setBodyText('');
      setBodyExamples([]);
      setFooterEnabled(false);
      setFooterText('');
      setButtonsEnabled(false);
      setButtons([]);
    }
  }, [template, open]);

  useEffect(() => {
    if (bodyVariables.length > bodyExamples.length) {
      const newExamples = [...bodyExamples];
      while (newExamples.length < bodyVariables.length) {
        const idx = newExamples.length + 1;
        newExamples.push(t('templateForm.exampleValue', { n: idx }));
      }
      setBodyExamples(newExamples);
    } else if (bodyVariables.length < bodyExamples.length) {
      setBodyExamples(bodyExamples.slice(0, bodyVariables.length));
    }
  }, [bodyVariables.length]);

  useEffect(() => {
    if (headerVariables.length > headerExamples.length) {
      const newExamples = [...headerExamples];
      while (newExamples.length < headerVariables.length) {
        const idx = newExamples.length + 1;
        newExamples.push(t('templateForm.exampleValue', { n: idx }));
      }
      setHeaderExamples(newExamples);
    } else if (headerVariables.length < headerExamples.length) {
      setHeaderExamples(headerExamples.slice(0, headerVariables.length));
    }
  }, [headerVariables.length]);

  const buildComponents = (): MetaTemplateComponent[] => {
    const components: MetaTemplateComponent[] = [];

    if (headerEnabled && headerFormat === 'TEXT') {
      const comp: MetaTemplateComponent = { type: 'HEADER', format: 'TEXT', text: headerText };
      if (headerVariables.length > 0) {
        comp.example = { header_text: [headerExamples.slice(0, headerVariables.length)] };
      }
      components.push(comp);
    } else if (headerEnabled) {
      components.push({ type: 'HEADER', format: headerFormat });
    }

    const bodyComp: MetaTemplateComponent = { type: 'BODY', text: bodyText };
    if (bodyVariables.length > 0) {
      bodyComp.example = { body_text: [bodyExamples.slice(0, bodyVariables.length)] };
    }
    components.push(bodyComp);

    if (footerEnabled) {
      components.push({ type: 'FOOTER', text: footerText });
    }

    if (buttonsEnabled && buttons.length > 0) {
      components.push({
        type: 'BUTTONS',
        buttons: buttons.map(b => {
          const btn: any = { type: b.type, text: b.text };
          if (b.type === 'URL') btn.url = b.url;
          return btn;
        }),
      });
    }

    return components;
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast({ title: t('templateForm.errorTitle'), description: t('templateForm.nameRequired'), variant: 'destructive' });
      return;
    }
    if (!bodyText.trim()) {
      toast({ title: t('templateForm.errorTitle'), description: t('templateForm.bodyRequired'), variant: 'destructive' });
      return;
    }
    if (!/^[a-z0-9_]+$/.test(name)) {
      toast({ title: t('templateForm.errorTitle'), description: t('templateForm.nameInvalid'), variant: 'destructive' });
      return;
    }

    if (bodyVariables.length > 0) {
      const emptyBodyExample = bodyExamples.slice(0, bodyVariables.length).some(e => !e.trim());
      if (emptyBodyExample) {
        toast({ title: t('templateForm.errorTitle'), description: t('templateForm.bodyExamplesRequired'), variant: 'destructive' });
        return;
      }
    }

    if (headerEnabled && headerFormat === 'TEXT' && headerVariables.length > 0) {
      const emptyHeaderExample = headerExamples.slice(0, headerVariables.length).some(e => !e.trim());
      if (emptyHeaderExample) {
        toast({ title: t('templateForm.errorTitle'), description: t('templateForm.headerExamplesRequired'), variant: 'destructive' });
        return;
      }
    }

    setSaving(true);
    try {
      const data: CreateTemplateData = {
        instanciaId,
        name: name.trim(),
        language,
        category,
        components: buildComponents(),
      };

      if (isEditing && template) {
        await instanciaService.atualizarTemplate(template.id, data);
        toast({ title: t('templateForm.updatedTitle'), description: t('templateForm.updatedDesc', { name }) });
      } else {
        await instanciaService.criarTemplate(data);
        toast({ title: t('templateForm.createdTitle'), description: t('templateForm.createdDesc', { name }) });
      }
      onSaved();
    } catch (err: any) {
      console.error('Erro ao salvar template:', err);
      toast({
        title: t('templateForm.saveErrorTitle'),
        description: err.response?.data?.message || t('templateForm.saveErrorDesc'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const addButton = () => {
    if (buttons.length >= 10) return;
    setButtons([...buttons, { type: 'QUICK_REPLY', text: '', url: '' }]);
  };

  const updateButton = (index: number, field: string, value: string) => {
    const updated = [...buttons];
    updated[index] = { ...updated[index], [field]: value };
    setButtons(updated);
  };

  const removeButton = (index: number) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col dark:bg-gray-800">
        <DialogHeader>
          <DialogTitle className="dark:text-white">
            {isEditing ? t('templateForm.editTitle') : t('templateForm.newTitle')}
          </DialogTitle>
          <DialogDescription className="dark:text-gray-400">
            {isEditing ? t('templateForm.editDesc') : t('templateForm.newDesc')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="dark:text-gray-300">{t('templateForm.name')}</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('templateForm.namePlaceholder')}
                disabled={isEditing}
                className="dark:bg-gray-700 dark:text-white"
              />
              <p className="text-xs text-muted-foreground">{t('templateForm.nameHint')}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="dark:text-gray-300">{t('templateForm.language')}</Label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full h-9 rounded-md border bg-white px-3 text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
              >
                {LANGUAGES.map(l => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="dark:text-gray-300">{t('templateForm.category')}</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full h-9 rounded-md border bg-white px-3 text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              {CATEGORIES.map(c => (
                <option key={c} value={c}>{t(`templates.category.${c}`)}</option>
              ))}
            </select>
          </div>

          <div className="border rounded-lg p-3 space-y-3 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <Label className="dark:text-gray-300 font-medium">{t('templateForm.header')}</Label>
              <label className="flex items-center gap-2 text-xs cursor-pointer dark:text-gray-400">
                <input type="checkbox" checked={headerEnabled} onChange={(e) => setHeaderEnabled(e.target.checked)} className="rounded" />
                {headerEnabled ? t('templateForm.enabled') : t('templateForm.disabled')}
              </label>
            </div>
            {headerEnabled && (
              <div className="space-y-2">
                <select
                  value={headerFormat}
                  onChange={(e) => setHeaderFormat(e.target.value)}
                  className="w-full h-9 rounded-md border bg-white px-3 text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600"
                >
                  {HEADER_FORMATS.map(f => (
                    <option key={f} value={f}>{t(`templateForm.headerFormats.${f}`)}</option>
                  ))}
                </select>
                {headerFormat === 'TEXT' && (
                  <>
                    <Input
                      value={headerText}
                      onChange={(e) => setHeaderText(e.target.value)}
                      placeholder={t('templateForm.headerTextPlaceholder')}
                      className="dark:bg-gray-700 dark:text-white"
                    />
                    {headerVariables.length > 0 && (
                      <div className="space-y-2 mt-2">
                        <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                          {t('templateForm.headerExamplesTitle')}
                        </p>
                        {headerVariables.map((v, idx) => (
                          <div key={v} className="flex items-center gap-2">
                            <Label className="text-xs text-muted-foreground w-16 shrink-0">{v}</Label>
                            <Input
                              value={headerExamples[idx] || ''}
                              onChange={(e) => {
                                const updated = [...headerExamples];
                                updated[idx] = e.target.value;
                                setHeaderExamples(updated);
                              }}
                              placeholder={t('templateForm.examplePlaceholder', { variable: v })}
                              className="h-8 text-xs dark:bg-gray-700 dark:text-white"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {headerFormat !== 'TEXT' && (
                  <p className="text-xs text-muted-foreground italic">
                    {t('templateForm.mediaHeaderNote')}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="border rounded-lg p-3 space-y-3 dark:border-gray-600">
            <Label className="dark:text-gray-300 font-medium">{t('templateForm.body')}</Label>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              placeholder={t('templateForm.bodyPlaceholder', VAR_PLACEHOLDERS)}
              rows={4}
              className="w-full rounded-md border bg-white px-3 py-2 text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600 resize-none"
            />
            {bodyVariables.length > 0 ? (
              <div className="space-y-2 mt-2">
                <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                  {t('templateForm.bodyExamplesTitle')}
                </p>
                {bodyVariables.map((v, idx) => (
                  <div key={v} className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground w-16 shrink-0">{v}</Label>
                    <Input
                      value={bodyExamples[idx] || ''}
                      onChange={(e) => {
                        const updated = [...bodyExamples];
                        updated[idx] = e.target.value;
                        setBodyExamples(updated);
                      }}
                      placeholder={t('templateForm.examplePlaceholder', { variable: v })}
                      className="h-8 text-xs dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t('templateForm.bodyHint', VAR_PLACEHOLDERS)}
              </p>
            )}
          </div>

          <div className="border rounded-lg p-3 space-y-3 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <Label className="dark:text-gray-300 font-medium">{t('templateForm.footer')}</Label>
              <label className="flex items-center gap-2 text-xs cursor-pointer dark:text-gray-400">
                <input type="checkbox" checked={footerEnabled} onChange={(e) => setFooterEnabled(e.target.checked)} className="rounded" />
                {footerEnabled ? t('templateForm.enabled') : t('templateForm.disabled')}
              </label>
            </div>
            {footerEnabled && (
              <Input
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                placeholder={t('templateForm.footerPlaceholder')}
                className="dark:bg-gray-700 dark:text-white"
              />
            )}
          </div>

          <div className="border rounded-lg p-3 space-y-3 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <Label className="dark:text-gray-300 font-medium">{t('templateForm.buttons')}</Label>
              <label className="flex items-center gap-2 text-xs cursor-pointer dark:text-gray-400">
                <input type="checkbox" checked={buttonsEnabled} onChange={(e) => setButtonsEnabled(e.target.checked)} className="rounded" />
                {buttonsEnabled ? t('templateForm.enabled') : t('templateForm.disabled')}
              </label>
            </div>
            {buttonsEnabled && (
              <div className="space-y-2">
                {buttons.map((btn, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <select
                      value={btn.type}
                      onChange={(e) => updateButton(idx, 'type', e.target.value)}
                      className="h-9 rounded-md border bg-white px-2 text-sm w-36 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    >
                      <option value="QUICK_REPLY">{t('templateForm.quickReply')}</option>
                      <option value="URL">URL</option>
                    </select>
                    <Input
                      value={btn.text}
                      onChange={(e) => updateButton(idx, 'text', e.target.value)}
                      placeholder={t('templateForm.buttonTextPlaceholder')}
                      className="flex-1 h-9 text-sm dark:bg-gray-700 dark:text-white"
                    />
                    {btn.type === 'URL' && (
                      <Input
                        value={btn.url}
                        onChange={(e) => updateButton(idx, 'url', e.target.value)}
                        placeholder={t('templateForm.urlPlaceholder', VAR_PLACEHOLDERS)}
                        className="flex-1 h-9 text-sm dark:bg-gray-700 dark:text-white"
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0 text-red-500 hover:text-red-700"
                      onClick={() => removeButton(idx)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={addButton}>
                  <Plus className="h-3 w-3 mr-1" />
                  {t('templateForm.addButton')}
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
          <HintTooltip text={t('templateForm.createHint')}>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isEditing ? t('common.save') : t('templateForm.create')}
            </Button>
          </HintTooltip>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MetaTemplateFormDialog;
